/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { css, jsx } from '@compiled/react';

import type { HierarchyData } from '@atlassian/reporting-lines/hierarchy-chart';

import type { Collaborator, PersonProfileData, RecentWorkUrls } from '../../types';
import { CardContainer } from './CardContainer';
import { CompactVariant } from './CompactVariant';
import { FullVariant } from './FullVariant';
import { LoadingSkeleton } from './LoadingSkeleton';

const containerRoot = css({
	width: '100%',
	containerType: 'inline-size',
	containerName: 'user-profile-preview-container',
});

const compactOnly = css({
	display: 'block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container user-profile-preview-container (min-width: 481px)': {
		display: 'none',
	},
});

const fullOnly = css({
	display: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container user-profile-preview-container (min-width: 481px)': {
		display: 'block',
	},
});

export interface ContentSwitchProps {
	isLoading: boolean;
	profileData?: PersonProfileData;
	hierarchyData?: HierarchyData;
	recentWorkData?: RecentWorkUrls;
	collaborators?: Collaborator[];
	testId?: string;
	cloudId: string;
	orgId?: string;
	siteId?: string;
	requestingUserId?: string;
	onLinkClick?: (
		url: string,
		index: number,
		opts?: { isAuxClick?: boolean; isContextMenu?: boolean },
	) => void;
}

/**
 * Internal component that switches between loading, error, and content variants based on state.
 * Renders both compact and full variants when data exists; CSS container queries control visibility.
 */
function ContentSwitch({
	isLoading,
	profileData,
	hierarchyData,
	recentWorkData,
	collaborators,
	testId,
	cloudId,
	orgId,
	siteId,
	requestingUserId,
	onLinkClick,
}: ContentSwitchProps): JSX.Element {
	return (
		<div css={containerRoot} data-testid={testId}>
			<CardContainer>
				{isLoading ? (
					<LoadingSkeleton />
				) : profileData ? (
					<>
						<div css={compactOnly} data-testid={testId ? `${testId}-layout-compact` : undefined}>
							<CompactVariant
								profileData={profileData}
								hierarchyData={hierarchyData}
								recentWorkData={recentWorkData}
								collaborators={collaborators}
								testId={testId}
								cloudId={cloudId}
								orgId={orgId}
								siteId={siteId}
								requestingUserId={requestingUserId}
								onLinkClick={onLinkClick}
							/>
						</div>
						<div css={fullOnly} data-testid={testId ? `${testId}-layout-full` : undefined}>
							<FullVariant
								profileData={profileData}
								hierarchyData={hierarchyData}
								recentWorkData={recentWorkData}
								collaborators={collaborators}
								testId={testId}
								cloudId={cloudId}
								orgId={orgId}
								siteId={siteId}
								requestingUserId={requestingUserId}
								onLinkClick={onLinkClick}
							/>
						</div>
					</>
				) : null}
			</CardContainer>
		</div>
	);
}

export { ContentSwitch };
